import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
  type CountryCode,
} from 'libphonenumber-js';
import { AREA_OF_LAW_LABELS } from './areaOfLaw';
import {
  formatTypedProfileTextField,
  formatTypedProfileTextFieldLive,
  formatTypedProfileTextFieldBlur,
  type TypedProfileTextField,
} from './textFormat';

export const TERMS_VERSION = '1.0';

export type AccountType = 'lawyer' | 'law_firm' | 'law_student';

export type AccountTypeIcon = 'Building2' | 'GraduationCap' | 'Briefcase';

export interface AccountTypeOption {
  id: AccountType;
  label: string;
  description: string;
  icon: AccountTypeIcon;
}

export const ACCOUNT_TYPE_OPTIONS: AccountTypeOption[] = [
  {
    id: 'lawyer',
    label: 'Lawyer',
    description: 'Individual advocate or practitioner',
    icon: 'Briefcase',
  },
  {
    id: 'law_firm',
    label: 'Law Firm / Organization',
    description: 'Set up a firm workspace for your team',
    icon: 'Building2',
  },
  {
    id: 'law_student',
    label: 'Law Student',
    description: 'Currently studying law',
    icon: 'GraduationCap',
  },
];

export type FieldType =
  | 'text'
  | 'single-select'
  | 'multi-select'
  | 'contact-info'
  | 'phone'
  | 'role-select';

export type OnboardingPhase = 'signup' | 'complete';

export type WorkSetting =
  | 'Independent Practitioner'
  | 'Law Firm'
  | 'Corporate In-house'
  | 'Law Student';

export const WORK_SETTING_OPTIONS: WorkSetting[] = [
  'Independent Practitioner',
  'Law Firm',
  'Corporate In-house',
  'Law Student',
];

export const FIRM_POSITION_OPTIONS = [
  'Managing Partner',
  'Partner',
  'Associate',
  'Intern',
] as const;

export const BAR_COUNCIL_OPTIONS = [
  'KPK Bar Council',
  'Islamabad Bar Council',
  'Punjab Bar Council',
  'Sindh Bar Council',
  'Balochistan Bar Council',
] as const;

export const DESIGNATION_OPTIONS = [
  'Advocate',
  'Advocate High Court',
  'Advocate Supreme Court',
] as const;

export const PROVINCE_OPTIONS = [
  'Islamabad Capital Territory',
  'Punjab',
  'Sindh',
  'Khyber Pakhtunkhwa',
  'Balochistan',
] as const;

export const PRIMARY_COURTS_OPTIONS = [
  'Supreme Court',
  'High Court',
  'District & Sessions Court',
  'Civil Court',
  'Special Courts (Banking, Custom, etc.)',
  'Tribunals',
] as const;

export const PRACTICE_AREAS_OPTIONS = [...AREA_OF_LAW_LABELS] as const;

export const HEAR_ABOUT_OPTIONS = [
  'Another Lawyer / Friend',
  'LinkedIn',
  'Facebook',
  'Google Search',
  'Instagram',
  'Other',
] as const;

export const YEAR_OF_STUDY_OPTIONS = [
  'LLB Year 1',
  'LLB Year 2',
  'LLB Year 3',
  'LLB Year 4',
  'LLB Year 5',
  'LLM',
  'Other',
] as const;

const LEGACY_WORK_SETTING_MAP: Record<string, WorkSetting> = {
  'Legal Practitioner': 'Independent Practitioner',
  'Law Firm / Organization': 'Law Firm',
};

const BAR_REG_PREFIX_BY_COUNCIL: Record<string, string> = {
  'Punjab Bar Council': 'PB',
  'Sindh Bar Council': 'SD',
  'KPK Bar Council': 'KP',
  'Balochistan Bar Council': 'BL',
  'Islamabad Bar Council': 'ISB',
};

const ACCOUNT_TYPE_TO_WORK_SETTING: Record<AccountType, WorkSetting> = {
  lawyer: 'Independent Practitioner',
  law_firm: 'Law Firm',
  law_student: 'Law Student',
};

export const TYPED_PROFILE_TEXT_FIELDS: readonly TypedProfileTextField[] = [
  'fullName',
  'lawCollege',
  'city',
  'firmName',
  'barRegNumber',
  'firmAffiliation',
  'courtStation',
];

export function formatProfileTextField(
  field: TypedProfileTextField,
  value: string,
): string {
  return formatTypedProfileTextField(field, value);
}

export function formatProfileTextFieldLive(
  field: TypedProfileTextField,
  value: string,
): string {
  return formatTypedProfileTextFieldLive(field, value);
}

export function formatProfileTextFieldBlur(
  field: TypedProfileTextField,
  value: string,
): string {
  return formatTypedProfileTextFieldBlur(field, value);
}

export function isTypedProfileTextField(
  field: string,
): field is TypedProfileTextField {
  return (TYPED_PROFILE_TEXT_FIELDS as readonly string[]).includes(field);
}

export interface ProfileData {
  accountType?: AccountType | string;
  fullName?: string;
  phone?: string;
  phoneCountry?: string;
  lawCollege?: string;
  barCouncil?: string;
  barRegNumber?: string;
  designation?: string;
  workSetting?: string;
  firmName?: string;
  firmPosition?: string;
  firmAffiliation?: string;
  province?: string;
  city?: string;
  primaryCourts?: string[];
  practiceAreas?: string[];
  hearAbout?: string;
  yearOfStudy?: string;
  courtRole?: string;
  courtStation?: string;
}

export interface OnboardingQuestion {
  id: string;
  title: string;
  subtitle?: string;
  type: FieldType;
  options?: readonly string[];
  placeholder?: string;
  required?: boolean;
}

export const ROLE_SELECT_QUESTION: OnboardingQuestion = {
  id: 'accountType',
  title: 'What best describes you?',
  type: 'role-select',
  required: true,
};

const FULL_NAME_QUESTION: OnboardingQuestion = {
  id: 'fullName',
  title: "What's your full name?",
  type: 'text',
  placeholder: 'Enter your full name',
  required: true,
};

const PHONE_QUESTION: OnboardingQuestion = {
  id: 'phone',
  title: "What's your phone number?",
  type: 'phone',
  required: true,
};

/** @deprecated Combined name+phone; use fullName + phone steps */
const CONTACT_QUESTION: OnboardingQuestion = {
  id: 'contact',
  title: "What's your full name and phone number?",
  type: 'contact-info',
  required: true,
};

const LAWYER_DESIGNATION_QUESTION: OnboardingQuestion = {
  id: 'designation',
  title: 'What is your designation?',
  type: 'single-select',
  options: [...DESIGNATION_OPTIONS],
  required: true,
};

const LAW_FIRM_POSITION_QUESTION: OnboardingQuestion = {
  id: 'firmPosition',
  title: 'What is your position in the law firm?',
  type: 'single-select',
  options: [...FIRM_POSITION_OPTIONS],
  required: true,
};

const LAW_STUDENT_COLLEGE_SIGNUP_QUESTION: OnboardingQuestion = {
  id: 'lawCollege',
  title: 'Which law college or university do you attend?',
  type: 'text',
  placeholder: 'Khyber Law College',
  required: true,
};

const ROLE_STEP_THREE_ID: Partial<Record<AccountType, string>> = {
  lawyer: 'designation',
  law_firm: 'firmPosition',
  law_student: 'lawCollege',
};

const LAWYER_LAW_COLLEGE_QUESTION: OnboardingQuestion = {
  id: 'lawCollege',
  title: 'Which law college or university did you attend?',
  type: 'text',
  placeholder: 'Khyber Law College',
  required: true,
};

const PROVINCE_QUESTION: OnboardingQuestion = {
  id: 'province',
  title: 'Which province are you based in?',
  type: 'single-select',
  options: [...PROVINCE_OPTIONS],
  required: true,
};

const CITY_QUESTION: OnboardingQuestion = {
  id: 'city',
  title: 'Which city or district?',
  type: 'text',
  placeholder: 'e.g., Peshawar...',
  required: true,
};

const HEAR_ABOUT_QUESTION: OnboardingQuestion = {
  id: 'hearAbout',
  title: 'Where did you hear about us?',
  type: 'single-select',
  options: [...HEAR_ABOUT_OPTIONS],
  required: true,
};

const PRACTICE_AREAS_QUESTION: OnboardingQuestion = {
  id: 'practiceAreas',
  title: 'What are your primary practice areas?',
  type: 'multi-select',
  options: [...PRACTICE_AREAS_OPTIONS],
  required: true,
};

const PRACTICE_AREAS_STUDENT_QUESTION: OnboardingQuestion = {
  ...PRACTICE_AREAS_QUESTION,
  title: "Which areas of law are you interested in?",
};

export const SIGNUP_QUESTIONS_BY_ROLE: Record<AccountType, OnboardingQuestion[]> = {
  lawyer: [FULL_NAME_QUESTION, LAWYER_DESIGNATION_QUESTION],
  law_firm: [FULL_NAME_QUESTION, LAW_FIRM_POSITION_QUESTION],
  law_student: [FULL_NAME_QUESTION, LAW_STUDENT_COLLEGE_SIGNUP_QUESTION],
};

export const DEFERRED_QUESTIONS_BY_ROLE: Record<AccountType, OnboardingQuestion[]> = {
  lawyer: [
    PHONE_QUESTION,
    LAWYER_LAW_COLLEGE_QUESTION,
    {
      id: 'barCouncil',
      title: 'Which Bar Council are you enrolled with?',
      type: 'single-select',
      options: [...BAR_COUNCIL_OPTIONS],
      required: true,
    },
    PROVINCE_QUESTION,
    CITY_QUESTION,
    {
      id: 'primaryCourts',
      title: 'Select your primary courts of practice:',
      type: 'multi-select',
      options: [...PRIMARY_COURTS_OPTIONS],
      required: true,
    },
    PRACTICE_AREAS_QUESTION,
    HEAR_ABOUT_QUESTION,
  ],
  law_firm: [
    PHONE_QUESTION,
    {
      id: 'firmName',
      title: 'What is the name of your Law Firm / Organization?',
      type: 'text',
      placeholder: 'Enter name...',
      required: true,
    },
    PROVINCE_QUESTION,
    CITY_QUESTION,
    {
      ...PRACTICE_AREAS_QUESTION,
      title: "What are your firm's main practice areas?",
    },
    HEAR_ABOUT_QUESTION,
  ],
  law_student: [
    PHONE_QUESTION,
    {
      id: 'yearOfStudy',
      title: 'What year are you in?',
      type: 'single-select',
      options: [...YEAR_OF_STUDY_OPTIONS],
      required: true,
    },
    PROVINCE_QUESTION,
    CITY_QUESTION,
    PRACTICE_AREAS_STUDENT_QUESTION,
    HEAR_ABOUT_QUESTION,
  ],
};

/** Full profile flow (signup + deferred) for backward compatibility */
export const QUESTIONS_BY_ROLE: Record<AccountType, OnboardingQuestion[]> = {
  lawyer: [...SIGNUP_QUESTIONS_BY_ROLE.lawyer, ...DEFERRED_QUESTIONS_BY_ROLE.lawyer],
  law_firm: [...SIGNUP_QUESTIONS_BY_ROLE.law_firm, ...DEFERRED_QUESTIONS_BY_ROLE.law_firm],
  law_student: [...SIGNUP_QUESTIONS_BY_ROLE.law_student, ...DEFERRED_QUESTIONS_BY_ROLE.law_student],
};

export const QUESTION_SIDEBAR_LABELS: Record<string, string> = {
  accountType: 'Your Role',
  fullName: 'Name',
  phone: 'Phone',
  contact: 'Personal Details',
  lawCollege: 'Education',
  barCouncil: 'Bar Council',
  barRegNumber: 'Registration No.',
  designation: 'Designation',
  workSetting: 'Practice Type',
  firmName: 'Firm Name',
  firmPosition: 'Position',
  firmAffiliation: 'Affiliation',
  province: 'Province',
  city: 'City',
  primaryCourts: 'Courts',
  practiceAreas: 'Practice Areas',
  hearAbout: 'Discovery',
  yearOfStudy: 'Year of Study',
  courtRole: 'Position',
  courtStation: 'Workplace',
};

export type ValidationResult = {
  valid: boolean;
  errors: Record<string, string>;
};

export function normalizeAccountType(
  value: string | undefined,
  workSetting?: string,
): AccountType | undefined {
  if (value === 'court_assistant' || value === 'legal_assistant') return undefined;
  if (value === 'lawyer' || value === 'law_firm' || value === 'law_student') {
    return value;
  }
  const ws = workSetting ? normalizeWorkSetting(workSetting) : undefined;
  if (ws === 'Law Firm') return 'law_firm';
  if (ws === 'Law Student') return 'law_student';
  if (ws === 'Independent Practitioner' || ws === 'Corporate In-house') return 'lawyer';
  if (workSetting === 'Legal Practitioner') return 'lawyer';
  if (workSetting === 'Law Firm / Organization') return 'law_firm';
  return undefined;
}

export function normalizeWorkSetting(value: string | undefined): WorkSetting | undefined {
  if (!value) return undefined;
  if (WORK_SETTING_OPTIONS.includes(value as WorkSetting)) {
    return value as WorkSetting;
  }
  return LEGACY_WORK_SETTING_MAP[value];
}

export function accountTypeToWorkSetting(accountType: AccountType | undefined): WorkSetting | undefined {
  if (!accountType) return undefined;
  return ACCOUNT_TYPE_TO_WORK_SETTING[accountType];
}

export function accountTypeToWorkspaceType(accountType: AccountType | undefined): 'solo' | 'firm' {
  return accountType === 'law_firm' ? 'firm' : 'solo';
}

/** @deprecated Use accountType === 'law_firm' */
export function requiresFirmName(
  accountTypeOrWorkSetting: string | undefined,
  workSetting?: string,
): boolean {
  const at = normalizeAccountType(accountTypeOrWorkSetting, workSetting ?? accountTypeOrWorkSetting);
  if (at) return at === 'law_firm';
  return normalizeWorkSetting(workSetting ?? accountTypeOrWorkSetting) === 'Law Firm';
}

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  lawyer: 'Lawyer',
  law_firm: 'Law Firm / Organization',
  law_student: 'Law Student',
};

/** @deprecated Use accountTypeToWorkspaceType */
export function workSettingToWorkspaceType(workSetting: string | undefined): 'solo' | 'firm' {
  return normalizeWorkSetting(workSetting) === 'Law Firm' ? 'firm' : 'solo';
}

export function getRoleStepThreeId(accountType?: AccountType | string): string | undefined {
  const role = normalizeAccountType(accountType);
  if (!role) return undefined;
  return ROLE_STEP_THREE_ID[role];
}

export function getSignupQuestions(accountType?: AccountType | string): OnboardingQuestion[] {
  const role = normalizeAccountType(accountType);
  if (!role) return [];
  return SIGNUP_QUESTIONS_BY_ROLE[role];
}

export function getDeferredQuestions(accountType?: AccountType | string): OnboardingQuestion[] {
  const role = normalizeAccountType(accountType);
  if (!role) return [];
  return DEFERRED_QUESTIONS_BY_ROLE[role];
}

export function getOnboardingQuestions(
  accountType?: AccountType | string,
  phase?: OnboardingPhase,
): OnboardingQuestion[] {
  const role = normalizeAccountType(accountType);
  if (!role) return [ROLE_SELECT_QUESTION];
  if (phase === 'signup') {
    return [ROLE_SELECT_QUESTION, ...SIGNUP_QUESTIONS_BY_ROLE[role]];
  }
  if (phase === 'complete') {
    return getDeferredQuestions(role);
  }
  return [ROLE_SELECT_QUESTION, ...QUESTIONS_BY_ROLE[role]];
}

function validateQuestionsList(
  formData: ProfileData,
  list: OnboardingQuestion[],
): ValidationResult {
  const allErrors: Record<string, string> = {};
  for (let i = 0; i < list.length; i++) {
    const q = list[i];
    if (q.required === false && q.type === 'text') {
      const val = trimStr(formData[q.id as keyof ProfileData] as string);
      if (!val) continue;
    }
    const { errors } = validateStep(i, formData, list);
    Object.assign(allErrors, errors);
  }
  return { valid: Object.keys(allErrors).length === 0, errors: allErrors };
}

export function validateSignupProfile(formData: ProfileData): ValidationResult {
  const role = normalizeAccountType(formData.accountType);
  if (!role) {
    return { valid: false, errors: { accountType: 'Please select your role' } };
  }
  const list = [ROLE_SELECT_QUESTION, ...SIGNUP_QUESTIONS_BY_ROLE[role]];
  return validateQuestionsList(formData, list);
}

export function validateDeferredProfile(formData: ProfileData): ValidationResult {
  const role = normalizeAccountType(formData.accountType);
  if (!role) {
    return { valid: false, errors: { accountType: 'Please select your role' } };
  }
  return validateQuestionsList(formData, DEFERRED_QUESTIONS_BY_ROLE[role]);
}

export function isProfileComplete(formData: ProfileData): boolean {
  const role = normalizeAccountType(formData.accountType);
  if (!role) return false;
  return validateQuestionsList(formData, QUESTIONS_BY_ROLE[role]).valid;
}

export function clearRoleSpecificFields(
  data: ProfileData,
  keepContact = true,
): ProfileData {
  const next: ProfileData = keepContact
    ? {
        accountType: data.accountType,
        fullName: data.fullName,
        phone: data.phone,
        phoneCountry: data.phoneCountry || 'PK',
      }
    : { phoneCountry: data.phoneCountry || 'PK' };

  return next;
}

/** Keep signup fields when role changes mid-flow */
export function clearDeferredFieldsOnRoleChange(
  data: ProfileData,
  newAccountType: AccountType,
): ProfileData {
  const kept = clearRoleSpecificFields(data, true);
  return { ...kept, accountType: newAccountType };
}

function trimStr(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function validateFullName(fullName: string | undefined): string | null {
  const name = trimStr(fullName);
  if (!name) return 'Full name is required';
  if (name.length < 2) return 'Full name must be at least 2 characters';
  if (!/^[\p{L}\s'.-]+$/u.test(name)) {
    return 'Full name can only contain letters, spaces, hyphens, and apostrophes';
  }
  return null;
}

export function validatePhone(
  phone: string | undefined,
  country: string | undefined,
): string | null {
  const raw = trimStr(phone);
  if (!raw) return 'Phone number is required';
  const countryCode = (country || 'PK').toUpperCase() as CountryCode;

  if (countryCode === 'PK') {
    const digits = raw.replace(/\D/g, '').replace(/^92/, '');
    if (digits.length !== 10) {
      return 'Enter a 10-digit mobile number';
    }
    if (!/^3\d{9}$/.test(digits)) {
      return 'Enter a valid Pakistani mobile number (e.g. 3001234567)';
    }
    return null;
  }

  try {
    if (!isValidPhoneNumber(raw, countryCode)) {
      return 'Enter a valid phone number for the selected country';
    }
  } catch {
    return 'Enter a valid phone number for the selected country';
  }
  return null;
}

function validateBarRegNumber(
  barRegNumber: string | undefined,
  barCouncil: string | undefined,
): string | null {
  const reg = trimStr(barRegNumber);
  if (!reg) return null;
  const genericPattern = /^[A-Z]{2,4}-[\dA-Z]+$/i;
  if (!genericPattern.test(reg)) {
    return 'Use format like PB-12345 (letters, hyphen, then numbers)';
  }
  const prefix = barCouncil ? BAR_REG_PREFIX_BY_COUNCIL[barCouncil] : undefined;
  if (prefix && !reg.toUpperCase().startsWith(prefix.toUpperCase())) {
    return `Registration number for ${barCouncil} usually starts with ${prefix}-`;
  }
  return null;
}

function validateTextField(
  value: string | undefined,
  label: string,
  minLength = 2,
): string | null {
  const v = trimStr(value);
  if (!v) return `${label} is required`;
  if (v.length < minLength) return `${label} must be at least ${minLength} characters`;
  return null;
}

function validateOptionalTextField(
  value: string | undefined,
  label: string,
  minLength = 2,
): string | null {
  const v = trimStr(value);
  if (!v) return null;
  if (v.length < minLength) return `${label} must be at least ${minLength} characters`;
  return null;
}

function validateEnum(
  value: string | undefined,
  options: readonly string[],
  label: string,
): string | null {
  if (!value || !options.includes(value)) return `Please select a ${label}`;
  return null;
}

function validateMultiSelect(
  value: string[] | undefined,
  label: string,
): string | null {
  if (!value || value.length === 0) return `Select at least one ${label}`;
  return null;
}

function validateQuestion(q: OnboardingQuestion, formData: ProfileData): Record<string, string> {
  const errors: Record<string, string> = {};

  switch (q.id) {
    case 'accountType': {
      if (!normalizeAccountType(formData.accountType)) {
        errors.accountType = 'Please select your role';
      }
      break;
    }
    case 'fullName': {
      const nameErr = validateFullName(formData.fullName);
      if (nameErr) errors.fullName = nameErr;
      break;
    }
    case 'phone': {
      const phoneErr = validatePhone(formData.phone, formData.phoneCountry);
      if (phoneErr) errors.phone = phoneErr;
      break;
    }
    case 'contact': {
      const nameErr = validateFullName(formData.fullName);
      if (nameErr) errors.fullName = nameErr;
      const phoneErr = validatePhone(formData.phone, formData.phoneCountry);
      if (phoneErr) errors.phone = phoneErr;
      break;
    }
    case 'lawCollege': {
      const err = validateTextField(formData.lawCollege, 'Law college');
      if (err) errors.lawCollege = err;
      break;
    }
    case 'barCouncil': {
      const err = validateEnum(formData.barCouncil, BAR_COUNCIL_OPTIONS, 'bar council');
      if (err) errors.barCouncil = err;
      break;
    }
    case 'barRegNumber': {
      const err = validateBarRegNumber(formData.barRegNumber, formData.barCouncil);
      if (err) errors.barRegNumber = err;
      break;
    }
    case 'designation': {
      const err = validateEnum(formData.designation, DESIGNATION_OPTIONS, 'designation');
      if (err) errors.designation = err;
      break;
    }
    case 'firmName': {
      const err = validateTextField(formData.firmName, 'Firm name');
      if (err) errors.firmName = err;
      break;
    }
    case 'firmPosition': {
      const err = validateEnum(formData.firmPosition, FIRM_POSITION_OPTIONS, 'position');
      if (err) errors.firmPosition = err;
      break;
    }
    case 'firmAffiliation': {
      const err = validateOptionalTextField(formData.firmAffiliation, 'Firm affiliation');
      if (err) errors.firmAffiliation = err;
      break;
    }
    case 'province': {
      const err = validateEnum(formData.province, PROVINCE_OPTIONS, 'province');
      if (err) errors.province = err;
      break;
    }
    case 'city': {
      const err = validateTextField(formData.city, 'City');
      if (err) errors.city = err;
      break;
    }
    case 'primaryCourts': {
      const err = validateMultiSelect(formData.primaryCourts, 'court');
      if (err) errors.primaryCourts = err;
      break;
    }
    case 'practiceAreas': {
      const err = validateMultiSelect(formData.practiceAreas, 'practice area');
      if (err) errors.practiceAreas = err;
      break;
    }
    case 'hearAbout': {
      const err = validateEnum(formData.hearAbout, HEAR_ABOUT_OPTIONS, 'option');
      if (err) errors.hearAbout = err;
      break;
    }
    case 'yearOfStudy': {
      const err = validateEnum(formData.yearOfStudy, YEAR_OF_STUDY_OPTIONS, 'year');
      if (err) errors.yearOfStudy = err;
      break;
    }
    default:
      break;
  }

  return errors;
}

export function validateStep(
  stepIndex: number,
  formData: ProfileData,
  questions?: OnboardingQuestion[],
): ValidationResult {
  const list = questions ?? getOnboardingQuestions(formData.accountType);
  const q = list[stepIndex];
  if (!q) return { valid: true, errors: {} };

  if (q.required === false && q.type === 'text') {
    const val = trimStr(formData[q.id as keyof ProfileData] as string);
    if (!val) return { valid: true, errors: {} };
  }

  const errors = validateQuestion(q, formData);
  return { valid: Object.keys(errors).length === 0, errors };
}

export function validateProfile(formData: ProfileData): ValidationResult {
  const role = normalizeAccountType(formData.accountType);
  if (!role) {
    return { valid: false, errors: { accountType: 'Please select your role' } };
  }
  return validateQuestionsList(formData, QUESTIONS_BY_ROLE[role]);
}

const FIELDS_BY_ROLE: Record<AccountType, (keyof ProfileData)[]> = {
  lawyer: [
    'accountType',
    'fullName',
    'phone',
    'phoneCountry',
    'designation',
    'lawCollege',
    'barCouncil',
    'province',
    'city',
    'primaryCourts',
    'practiceAreas',
    'hearAbout',
    'workSetting',
  ],
  law_firm: [
    'accountType',
    'fullName',
    'phone',
    'phoneCountry',
    'firmPosition',
    'firmName',
    'province',
    'city',
    'practiceAreas',
    'hearAbout',
    'workSetting',
  ],
  law_student: [
    'accountType',
    'fullName',
    'phone',
    'phoneCountry',
    'lawCollege',
    'yearOfStudy',
    'province',
    'city',
    'practiceAreas',
    'hearAbout',
    'workSetting',
  ],
};

export function normalizeProfileData(formData: ProfileData): ProfileData {
  const accountType = normalizeAccountType(formData.accountType, formData.workSetting);
  const country = (formData.phoneCountry || 'PK').toUpperCase() as CountryCode;
  let phone = trimStr(formData.phone);

  if (phone) {
    if (country === 'PK') {
      const digits = phone.replace(/\D/g, '').replace(/^92/, '');
      if (digits.length === 10) {
        phone = `+92${digits}`;
      }
    } else {
      const parsed = parsePhoneNumberFromString(phone, country);
      if (parsed?.isValid()) {
        phone = parsed.format('E.164');
      }
    }
  }

  const workSetting = accountType ? accountTypeToWorkSetting(accountType) : normalizeWorkSetting(formData.workSetting);

  const base: ProfileData = {
    accountType,
    fullName: formatTypedProfileTextField('fullName', trimStr(formData.fullName)),
    phone,
    phoneCountry: country,
    workSetting,
    province: formData.province,
    city: formatTypedProfileTextField('city', trimStr(formData.city)),
    hearAbout: formData.hearAbout,
  };

  if (!accountType) {
    return { ...formData, ...base };
  }

  const allowed = new Set(FIELDS_BY_ROLE[accountType]);
  const merged: ProfileData = { ...base };

  for (const key of allowed) {
    const val = formData[key];
    if (val === undefined || val === '') continue;

    if (key === 'lawCollege' && typeof val === 'string') {
      merged.lawCollege = formatTypedProfileTextField('lawCollege', val);
    } else if (key === 'firmName' && typeof val === 'string') {
      merged.firmName = formatTypedProfileTextField('firmName', val);
    } else if (key === 'firmAffiliation' && typeof val === 'string') {
      const aff = trimStr(val);
      if (aff) merged.firmAffiliation = formatTypedProfileTextField('firmAffiliation', aff);
    } else if (key === 'barRegNumber' && typeof val === 'string') {
      const reg = trimStr(val);
      if (reg) merged.barRegNumber = formatTypedProfileTextField('barRegNumber', reg);
    } else {
      (merged as Record<string, unknown>)[key] = val;
    }
  }

  return merged;
}

export function isStepCompleted(
  stepIndex: number,
  formData: ProfileData,
  questions?: OnboardingQuestion[],
): boolean {
  const list = questions ?? getOnboardingQuestions(formData.accountType);
  const q = list[stepIndex];
  if (!q) return false;
  if (q.id === 'accountType') return !!normalizeAccountType(formData.accountType);
  return validateStep(stepIndex, formData, list).valid;
}

export function getNextStepIndex(
  currentStep: number,
  _formData: ProfileData,
  questions?: OnboardingQuestion[],
): number {
  const list = questions ?? getOnboardingQuestions(_formData.accountType);
  return Math.min(currentStep + 1, list.length);
}

export function getPrevStepIndex(currentStep: number): number {
  return Math.max(currentStep - 1, 0);
}

export function applyAccountTypeChange(
  prev: ProfileData,
  newAccountType: AccountType,
): ProfileData {
  if (prev.accountType === newAccountType) {
    return { ...prev, accountType: newAccountType };
  }
  return clearDeferredFieldsOnRoleChange(prev, newAccountType);
}

export function getFirstIncompleteDeferredStepIndex(
  formData: ProfileData,
  questions?: OnboardingQuestion[],
): number {
  const list = questions ?? getDeferredQuestions(formData.accountType);
  for (let i = 0; i < list.length; i++) {
    if (!isStepCompleted(i, formData, list)) return i;
  }
  return 0;
}

/** @deprecated */
export const ONBOARDING_QUESTIONS = getOnboardingQuestions('lawyer');

/** @deprecated */
export function shouldHideFirmNameStep(_formData: ProfileData): boolean {
  return false;
}
